'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils/cn'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-theme-border-primary bg-theme-bg-secondary text-theme-text-primary hover:bg-theme-bg-tertiary hover:text-theme-text-primary backdrop-blur-glass',
        secondary:
          'bg-theme-bg-elevated text-theme-text-primary border border-theme-border-primary hover:bg-theme-bg-secondary backdrop-blur-glass shadow-theme-sm',
        ghost: 'hover:bg-theme-bg-secondary text-theme-text-primary',
        link: 'text-primary underline-offset-4 hover:underline',
        glass: 'border border-theme-border-primary bg-theme-bg-elevated text-theme-text-primary hover:bg-theme-bg-secondary backdrop-blur-glass shadow-theme-sm'
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const resolvedType = type ?? 'button'

    const componentProps = asChild
      ? props
      : { type: resolvedType, ...props }

    return (
      <AppErrorBoundary source="ui-button">
        {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...componentProps}
        />
      </AppErrorBoundary>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }